import React, { useState, useEffect } from 'react';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { supabase } from '@/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/app/components/Header';
import { Footer } from '@/app/components/Footer';
import {
  Chat,
  Channel,
  Window,
  MessageList,
  MessageInput,
} from 'stream-chat-react';
import useChatClient from '@/hooks/useChatClient';
import 'stream-chat-react/dist/css/v2/index.css';
import * as Sentry from '@sentry/browser';

function ChatLoading() {
  return (
    <div className="flex items-center gap-2 text-indigo-600">
      <div className="w-4 h-4 border-2 border-t-transparent border-indigo-600 rounded-full animate-spin" />
      <span className="text-sm font-medium">Connecting to support...</span>
    </div>
  );
}

function CustomChannelHeader() {
  return (
    <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
        Customer Support
      </h3>
      <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' }}>
        We're here to help! Ask us anything about Zaptopia.
      </p>
    </div>
  );
}

export function AccountPage() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [error, setError] = useState('');

  // Customer support chat state
  const { client, channel, connectChat, disconnectChat } = useChatClient();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);

  // Redirect if not authenticated (but wait for auth loading to complete)
  useEffect(() => {
    if (!authLoading && !user) {
      console.log('User not authenticated, redirecting to home');
      navigate('/');
      return;
    }
  }, [user, authLoading, navigate]);

  // Load user profile
  useEffect(() => {
    if (authLoading || !user) return;

    const loadProfile = async () => {
      console.log('Loading user profile');
      setIsLoading(true);
      setError('');
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await fetch('/api/profile', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          // Handle authentication errors
          if (response.status === 401 || response.status === 403) {
            console.warn('Authentication error in profile loading, signing out user');
            try {
              await signOut();
              navigate('/');
              return;
            } catch (signOutError) {
              console.error('Error signing out user:', signOutError);
            }
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const profileData = await response.json();
        console.log('Loaded profile:', profileData);
        
        setProfile(profileData);
        setDisplayName(profileData.displayName || '');
      } catch (error) {
        console.error('Error loading profile:', error);
        Sentry.captureException(error, {
          extra: { userEmail: user.email }
        });
        setError('Failed to load profile. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user, authLoading]);

  // Cleanup chat when component unmounts
  useEffect(() => {
    return () => {
      if (client) {
        disconnectChat();
      }
    };
  }, [client, disconnectChat]);

  const handleSaveDisplayName = async (e) => {
    e.preventDefault();
    
    if (!displayName.trim()) {
      setError('Display name cannot be empty');
      return;
    }

    console.log('Saving display name:', displayName);
    setIsSaving(true);
    setError('');
    setSaveMessage('');
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch('/api/profile', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          displayName: displayName.trim()
        }),
      });

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          console.warn('Authentication error in profile saving, signing out user');
          try {
            await signOut();
            navigate('/');
            return;
          } catch (signOutError) {
            console.error('Error signing out user:', signOutError);
          }
        }
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const updatedProfile = await response.json();
      console.log('Updated profile:', updatedProfile);
      
      setProfile(updatedProfile);
      setSaveMessage('Display name saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving display name:', error);
      Sentry.captureException(error, {
        extra: { 
          userEmail: user.email,
          displayName: displayName.trim()
        }
      });
      setError(error.message || 'Failed to save display name. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignOut = async () => {
    console.log('Signing out user');
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      Sentry.captureException(error);
      setError('Failed to sign out. Please try again.');
    }
  };

  const toggleCustomerSupport = async () => {
    if (!isChatOpen) {
      setIsChatLoading(true);
      try {
        console.log('Opening customer support chat');
        await connectChat();
        setIsChatOpen(true);
      } catch (error) {
        console.error('Error connecting to customer support:', error);
        Sentry.captureException(error);
        setError('Failed to connect to customer support. Please try again.');
      } finally {
        setIsChatLoading(false);
      }
    } else {
      console.log('Closing customer support chat');
      setIsChatOpen(false);
      await disconnectChat();
    }
  };

  // Show loading while auth is still loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-t-transparent border-indigo-500 rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-20 h-20 border-4 border-t-transparent border-purple-400 rounded-full animate-spin opacity-60" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
        </div>
      </div>
    );
  }

  // Don't render anything if user is not authenticated (redirect will happen via useEffect)
  if (!user) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-t-transparent border-indigo-500 rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-20 h-20 border-4 border-t-transparent border-purple-400 rounded-full animate-spin opacity-60" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/15 to-purple-600/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/15 to-pink-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-violet-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <Header />
      
      <div className="relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <button
                onClick={() => navigate('/')}
                className="group inline-flex items-center text-indigo-600 hover:text-indigo-800 font-semibold mb-8 transition-all duration-300 transform hover:scale-105"
              >
                <svg className="w-5 h-5 mr-2 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Zaptopia
              </button>
              
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
                  <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                    Account Settings
                  </span>
                </h1>
                <p className="text-xl sm:text-2xl text-gray-600 font-light">
                  Manage your profile and account preferences
                </p>
              </div>
            </div>

            {/* Account Card */}
            <div className="relative group mb-8">
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/30 via-purple-500/30 to-pink-500/30 rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
              <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 sm:p-12 shadow-2xl border border-white/50">
                {/* Profile Header */}
                <div className="flex items-center mb-12">
                  <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mr-6">
                    {profile?.displayName ? 
                      profile.displayName.charAt(0).toUpperCase() : 
                      user.email.charAt(0).toUpperCase()
                    }
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {profile?.displayName || 'Set your display name'}
                    </h2>
                    <p className="text-lg text-gray-600">{user.email}</p>
                  </div>
                </div>

                {/* Display Name Form */}
                <form onSubmit={handleSaveDisplayName} className="mb-12">
                  <div className="mb-6">
                    <label htmlFor="displayName" className="block text-lg font-semibold text-gray-900 mb-3">
                      Display Name
                    </label>
                    <input
                      type="text"
                      id="displayName"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="w-full px-6 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all duration-300 bg-gray-50/50 backdrop-blur-sm"
                      placeholder="Enter your display name"
                      maxLength={50}
                      required
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      This is how your name will appear to other users
                    </p>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 bg-red-50/80 backdrop-blur-sm border-2 border-red-200 rounded-2xl">
                      <p className="text-red-700 font-medium">{error}</p>
                    </div>
                  )}

                  {saveMessage && (
                    <div className="mb-6 p-4 bg-green-50/80 backdrop-blur-sm border-2 border-green-200 rounded-2xl">
                      <p className="text-green-700 font-medium">{saveMessage}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSaving || !displayName.trim()}
                    className="group px-8 py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-500 transform hover:scale-105 hover:-translate-y-1"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-700 via-purple-700 to-pink-700 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative flex items-center">
                      {isSaving ? (
                        <>
                          <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin mr-3"></div>
                          Saving...
                        </>
                      ) : (
                        'Save Display Name'
                      )}
                    </div>
                  </button>
                </form>

                {/* Account Information */}
                <div className="border-t border-gray-200 pt-12 mb-12">
                  <h3 className="text-2xl font-bold text-gray-900 mb-8">Account Information</h3>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-6 border-2 border-indigo-100">
                      <label className="block text-sm font-semibold text-indigo-600 mb-2">Email Address</label>
                      <p className="text-lg text-gray-900 font-medium">{user.email}</p>
                    </div>
                    {profile?.createdAt && (
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-100">
                        <label className="block text-sm font-semibold text-purple-600 mb-2">Member Since</label>
                        <p className="text-lg text-gray-900 font-medium">
                          {new Date(profile.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Customer Support Section */}
                <div className="border-t border-gray-200 pt-12 mb-12">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Customer Support</h3>
                  <p className="text-lg text-gray-600 mb-8">
                    Need help with Zaptopia? Our support team is here to assist you with any questions or issues.
                  </p>
                  
                  {!isChatOpen ? (
                    <button
                      onClick={toggleCustomerSupport}
                      disabled={isChatLoading}
                      className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-500 transform hover:scale-105 hover:-translate-y-1"
                    >
                      <div className="relative flex items-center">
                        {isChatLoading ? (
                          <ChatLoading />
                        ) : (
                          <>
                            <svg className="w-6 h-6 mr-3 transition-transform group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a9.863 9.863 0 01-4.906-1.287A6 6 0 006 18L3 21l3-3a6 6 0 011.287-4.906A9.863 9.863 0 0112 21c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8" />
                            </svg>
                            Start Chat with Support
                          </>
                        )}
                      </div>
                    </button>
                  ) : (
                    <div className="border-2 border-gray-200 rounded-2xl overflow-hidden bg-white/50 backdrop-blur-sm">
                      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b-2 border-gray-200">
                        <span className="font-bold text-gray-900">Support Chat</span>
                        <button
                          onClick={toggleCustomerSupport}
                          className="text-gray-500 hover:text-gray-700 p-2 hover:bg-white/50 rounded-full transition-colors"
                          title="Close chat"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      {client && channel ? (
                        <div className="h-96">
                          <Chat client={client}>
                            <Channel channel={channel}>
                              <Window>
                                <CustomChannelHeader />
                                <MessageList />
                                <MessageInput placeholder="Type your message here..." />
                              </Window>
                            </Channel>
                          </Chat>
                        </div>
                      ) : (
                        <div className="h-96 flex items-center justify-center">
                          <ChatLoading />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Sign Out */}
                <div className="border-t border-gray-200 pt-12">
                  <button
                    onClick={handleSignOut}
                    className="group px-8 py-4 bg-white/80 backdrop-blur-sm text-gray-700 hover:text-gray-900 font-semibold text-lg rounded-2xl border-2 border-gray-300 hover:border-gray-400 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  >
                    <div className="flex items-center">
                      <svg className="w-6 h-6 mr-3 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign Out
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Made on ZAPT Badge */}
            <div className="text-center">
              <a
                href="https://www.zapt.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors duration-300"
              >
                Made on ZAPT
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}